'use client';

import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { useTranslations } from 'next-intl';

import { WorkbenchShareModal } from '@/components/ShareModal/WorkbenchShareModal';
import { prefetchSiteThumbnail } from '@/constants/shareLink';
import { useGetSharableLink } from '@/hooks/useGetSharableLink';
import { useResolvedShareUrl } from '@/hooks/useResolvedShareUrl';

interface HamburgerMenuProps {
  metaSiteId?: string;
  targetUrl?: string;
}

export function HamburgerMenu({ metaSiteId, targetUrl }: HamburgerMenuProps) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Keep the share-link cache on this menu, not the overlay. Closing the
  // modal unmounts WorkbenchShareModal; getSharableLink creates a new
  // feedback entity on every uncached call.
  const { getSharableLink } = useGetSharableLink(metaSiteId);
  const shareUrl = useResolvedShareUrl(getSharableLink, metaSiteId, shareOpen);

  useEffect(() => {
    prefetchSiteThumbnail(metaSiteId);
  }, [metaSiteId]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <>
      <div ref={menuRef} className="relative">
        <button
          type="button"
          data-hook="toolbar-menu-button"
          aria-label={t('get-feedback-shareMenu-menuLabel')}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground hover:bg-muted transition-colors"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.25 15.5C18.6642 15.5 19 15.8358 19 16.25C19 16.6642 18.6642 17 18.25 17H5.75C5.33579 17 5 16.6642 5 16.25C5 15.8358 5.33579 15.5 5.75 15.5H18.25Z"
              fill="currentColor"
            />
            <path
              d="M18.25 11.25C18.6642 11.25 19 11.5858 19 12C19 12.4142 18.6642 12.75 18.25 12.75H5.75C5.33579 12.75 5 12.4142 5 12C5 11.5858 5.33579 11.25 5.75 11.25H18.25Z"
              fill="currentColor"
            />
            <path
              d="M18.25 7C18.6642 7 19 7.33579 19 7.75C19 8.16421 18.6642 8.5 18.25 8.5H5.75C5.33579 8.5 5 8.16421 5 7.75C5 7.33579 5.33579 7 5.75 7H18.25Z"
              fill="currentColor"
            />
          </svg>
        </button>

        {open && (
          <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[180px] rounded-xl border border-border bg-background shadow-lg py-1 overflow-hidden">
            <button
              type="button"
              data-hook="toolbar-menu-request-feedback"
              onClick={() => {
                setOpen(false);
                setShareOpen(true);
              }}
              className="flex w-full items-center px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
            >
              {t('get-feedback-shareMenu-requestFeedbackAction')}
            </button>
          </div>
        )}
      </div>

      {shareOpen &&
        createPortal(
          <WorkbenchShareModal
            shareUrl={shareUrl}
            metaSiteId={metaSiteId}
            targetUrl={targetUrl}
            onClose={() => setShareOpen(false)}
          />,
          document.body,
        )}
    </>
  );
}
